import {
  BrowserAuthError,
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

import type { AgentConfig } from "./index";

/**
 * Entra sign-in for calling the Foundry hosted agent directly from the browser. The site must serve
 * a blank /auth-popup.html at its origin, registered as a redirect URI on the agent's app
 * registration — MSAL's popup lands there and the opener reads the result back.
 */
export function createFoundryAuth(agent: AgentConfig, getLoginHint: () => string | undefined) {
  let msalInstance: PublicClientApplication | undefined;
  // Single-flights concurrent acquireToken calls onto one loginPopup, since MSAL itself throws
  // interaction_in_progress rather than de-duping.
  let acquireTokenPromise: Promise<string> | undefined;

  async function acquireTokenNow(): Promise<string> {
    msalInstance ??= new PublicClientApplication({
      auth: {
        clientId: agent.clientId,
        authority: `${agent.authority ?? "https://login.microsoftonline.com"}/${agent.tenantId}`,
        redirectUri: `${window.location.origin}/auth-popup.html`,
      },
      // localStorage so a new tab reuses the signed-in account and refresh token instead of popping up
      // again (MSAL encrypts it with a session-cookie key). The site's COEP/frame-src block ssoSilent's
      // hidden iframe, so the refresh token is the only silent path available.
      cache: { cacheLocation: "localStorage" },
    });
    await msalInstance.initialize();

    // The site's own sign-in already knows who the user is — hinting Entra with the same account
    // lets the popup complete against the user's live Entra session without a picker.
    const loginRequest = {
      scopes: ["https://ai.azure.com/.default"],
      redirectUri: `${window.location.origin}/auth-popup.html`,
      loginHint: getLoginHint(),
    };

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length > 0) {
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
          // Keep silent cache/refresh-token renewal, but never fall back to a blocked login iframe.
          cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
        });
        return response.accessToken;
      } catch (error) {
        if (
          !(error instanceof InteractionRequiredAuthError) &&
          !(error instanceof BrowserAuthError && error.errorCode === "monitor_window_timeout")
        ) {
          throw error;
        }
      }
    }

    try {
      const response = await msalInstance.loginPopup(loginRequest);
      msalInstance.setActiveAccount(response.account);
      return response.accessToken;
    } catch (error) {
      // MSAL leaves a stale "interaction in progress" flag in sessionStorage if a previous popup
      // was interrupted instead of running to completion, and never clears it itself — removing
      // the sessionStorage key directly and retrying once is the documented workaround
      // (AzureAD/microsoft-authentication-library-for-js#3005).
      if (error instanceof BrowserAuthError && error.errorCode === "interaction_in_progress") {
        Object.keys(sessionStorage)
          .filter((key) => key.endsWith(".interaction.status"))
          .forEach((key) => sessionStorage.removeItem(key));

        const response = await msalInstance.loginPopup(loginRequest);
        msalInstance.setActiveAccount(response.account);
        return response.accessToken;
      }
      throw error;
    }
  }

  return function acquireToken(): Promise<string> {
    acquireTokenPromise ??= acquireTokenNow().finally(() => {
      acquireTokenPromise = undefined;
    });
    return acquireTokenPromise;
  };
}
